import React, { useEffect, useRef } from 'react';
import HomeHeader from '@/components/HomeHeader';
import Footer from '@/components/homepage/Footer';
import { usePageMeta } from '@/hooks/usePageMeta';
import TableOfContents from '@/components/resources/TableOfContents';
import EmailCapture from '@/components/resources/EmailCapture';

const TITLE = 'How to Ask for Support Over Text and Social Media (Without It Feeling Impersonal)';
const DESCRIPTION =
    "A practical guide to asking for missionary support over text message and social media — when it's the right channel, a three-message sequence that doesn't feel like a sales pitch, and what to avoid.";

const JSON_LD = {
    '@context': 'https://schema.org',
    '@graph': [
        {
            '@type': 'Article',
            headline: TITLE,
            description: DESCRIPTION,
            image: 'https://devotionfund.com/resources/cover6.jpg',
            datePublished: '2026-07-29',
            dateModified: '2026-07-29',
            author: { '@type': 'Organization', name: 'Devotion Fund', url: 'https://devotionfund.com' },
            publisher: {
                '@type': 'Organization',
                name: 'Devotion Fund',
                logo: { '@type': 'ImageObject', url: 'https://devotionfund.com/logo.png' },
            },
            mainEntityOfPage: {
                '@id': 'https://devotionfund.com/resources/how-to-ask-for-support-over-text-and-social-media',
                '@type': 'WebPage',
            },
        },
        {
            '@type': 'FAQPage',
            mainEntity: [
                {
                    '@type': 'Question',
                    name: 'Is it okay to ask for missionary support over text message?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "Yes, for people you already have a real relationship with — close friends, family, and people who already know about your calling. Text works best as a follow-up to something they've already heard about, not as the first place they learn you're raising support.",
                    },
                },
                {
                    '@type': 'Question',
                    name: 'Should I post about my support raising publicly on social media?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "A general update post works well to build awareness, but the actual ask should move to a direct message with specific people. Public posts are good for visibility; direct, personal asks are what actually convert into committed support.",
                    },
                },
                {
                    '@type': 'Question',
                    name: 'How is a text ask different from an email or letter ask?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "A text is shorter, more conversational, and works best in a back-and-forth rather than a one-shot pitch. It should read like a message from a friend, not a condensed version of your support letter, and it should invite a reply rather than a decision on the spot.",
                    },
                },
                {
                    '@type': 'Question',
                    name: "What if someone doesn't respond to a text ask?",
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "One gentle follow-up after a week or two is appropriate — people miss texts constantly. After that, let it go without pressure. A non-response is rarely a no; it's usually just a message that got buried.",
                    },
                },
            ],
        },
    ],
};

export default function HowToAskForSupportOverTextAndSocialMedia() {
    usePageMeta(TITLE, DESCRIPTION, { path: '/resources/how-to-ask-for-support-over-text-and-social-media' });

    const articleRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const el = document.createElement('script');
        el.type = 'application/ld+json';
        el.setAttribute('data-text-social-ask-jsonld', 'true');
        el.textContent = JSON.stringify(JSON_LD);
        document.head.appendChild(el);
        return () => {
            el.remove();
        };
    }, []);

    return (
        <div className="min-h-screen bg-white flex flex-col">
            <HomeHeader />
            <div className="w-full h-64 md:h-80 bg-gray-100 overflow-hidden">
                <img src="/resources/cover6.jpg" alt="A missionary texting a friend to ask for support" className="w-full h-full object-cover" />
            </div>
            <div className="max-w-3xl mx-auto px-4 py-12 w-full">
                <h1 className="text-3xl font-bold mb-6">{TITLE}</h1>
                <TableOfContents targetRef={articleRef} />
                <div className="prose max-w-none" ref={articleRef}>
                    <p>Most support-raising advice assumes a letter or a coffee meeting. But a lot of your real relationships live in text threads and group chats, and pretending those channels don't exist means leaving genuine potential support on the table. Text and social media asks work — but only when they follow the norms of those channels instead of trying to cram a formal letter into a text bubble.</p>

                    <h2>Why text and social media asks feel different from letters</h2>

                    <p>A support letter is a broadcast — the same message to everyone, read on the recipient's own time. A text is a conversation, expected to be short, personal, and to invite a reply. Sending a letter-length message by text feels like a mismatch the moment someone opens it, even if the content is identical. The channel sets the expectation before a single word is read.</p>

                    <h2>When a text or DM is the right channel (and when it isn't)</h2>

                    <p>Text and social DMs work best with people you already talk to that way — close friends, siblings, cousins, old roommates, people from a group chat you're already in. They work poorly as a first introduction to your calling; someone who's never heard you're headed to the field shouldn't learn it and get an ask in the same message. Save text for people who already have context, and use a letter, call, or in-person conversation to build that context first with everyone else.</p>

                    <h2>The three-message text sequence that doesn't feel like a sales pitch</h2>

                    <p>A single ask-heavy text reads like a form message even when it isn't one. A short sequence spread over a few days feels far more natural:</p>

                    <p><strong>Message one — the news, no ask.</strong> "Hey! Wanted you to be one of the first to know — I'm heading to [place] with [organization] this year to [one-line description]. Really excited and a little nervous. More soon!" This message alone often prompts a reply asking how they can help, which sets up message two naturally.</p>

                    <p><strong>Message two — the specific ask, a few days later.</strong> "I'm building my support team and would love for you to be part of it if it's something you'd feel led to do — even $25-50/month makes a real difference. No pressure at all, just wanted to personally ask you since you've meant a lot to me. Here's my page if you want to take a look: [link]." Naming a specific range removes the guesswork that keeps people from responding.</p>

                    <p><strong>Message three — a warm follow-up, only if needed.</strong> If there's no response after a week or two: "No worries if now's not the right time — just wanted to check if you saw this! Either way, I'd love to keep you updated on the trip." This gives an easy out and keeps the relationship intact regardless of their answer.</p>

                    <h2>How to ask on social media without posting a public "please give me money"</h2>

                    <p>A public post announcing your calling and linking your page is fine for awareness — it primes people before a more personal ask reaches them. But the post itself rarely converts well on its own; treat it as the equivalent of the support letter's mailing list, not the ask itself. The actual conversion happens in the DMs you send afterward to specific people you know would want to be part of it, using the same two-message sequence above. If you're unsure how to open that first conversation without it feeling awkward, the same mindset shift in <a href="/resources/how-to-ask-friends-and-family-to-support-your-mission-trip">asking friends and family to support your mission trip</a> applies here — you're inviting people into something meaningful, not asking a favor.</p>

                    <h2>What to avoid: mass texts, guilt language, and vague asks</h2>

                    <p>A few habits reliably undercut a text or social ask. A mass text sent to twenty people at once with no personalization reads as exactly that — a form message, no matter how warm the wording. Guilt-based language ("I really need this" or "I'm counting on you") pressures people into a yes they may resent rather than a genuine commitment. And a vague ask with no number or link forces the other person to do the work of figuring out how to help, which is usually where the conversation quietly dies. Be specific, be personal, and make the next step a single tap.</p>

                    <h2>Following up over text after a support letter</h2>

                    <p>Text works especially well as a lightweight follow-up to a letter someone already received but hasn't responded to. A short "Hey, did my letter about [place] reach you okay? No pressure, just wanted to check!" reopens the conversation without repeating the whole ask. This mirrors the same low-pressure approach covered in <a href="/resources/follow-up-after-support-raising-appointment">following up after a support-raising appointment</a> — the goal is always a genuine check-in, not a reminder that they owe you an answer.</p>

                    <h2>Frequently asked questions</h2>

                    <p><strong>Is it okay to ask for missionary support over text message?</strong><br />
                    Yes, for people you already have a real relationship with — it works best as a follow-up to something they've already heard about.</p>

                    <p><strong>Should I post about my support raising publicly on social media?</strong><br />
                    A public post builds awareness, but the actual ask should move to a direct, personal message.</p>

                    <p><strong>How is a text ask different from an email or letter ask?</strong><br />
                    Shorter, more conversational, and built for a back-and-forth rather than a one-shot pitch.</p>

                    <p><strong>What if someone doesn't respond to a text ask?</strong><br />
                    One gentle follow-up after a week or two is fine — after that, let it go without pressure.</p>

                    <h2>Next reads on Devotion Fund</h2>
                    <ul>
                        <li><a href="/resources/how-to-ask-friends-and-family-to-support-your-mission-trip">How to Ask Friends and Family to Support Your Mission Trip Without Feeling Awkward</a></li>
                        <li><a href="/resources/follow-up-after-support-raising-appointment">How to Follow Up After a Support-Raising Appointment (Without Being Pushy)</a></li>
                        <li><a href="/resources/introverts-guide-to-support-raising">The Introvert's Guide to Support Raising</a></li>
                    </ul>

                    <h2>About the author</h2>
                    <p>We're the team behind <a href="/">Devotion Fund</a>. We wrote this after seeing how many missionaries default to formal letters for every relationship, even the ones that have always lived in a text thread — and lose natural, easy asks in the process.</p>
                    <p><small><em>Last updated: 2026-07-29.</em></small></p>
                </div>
                <div className="mt-8">
                    <EmailCapture page="/resources/how-to-ask-for-support-over-text-and-social-media" />
                </div>
            </div>
            <Footer />
        </div>
    );
}
